import React, { useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface AccordionProps {
  title: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  id?: string;
  className?: string;
  /**
   * Fires on the native `toggle` event with the new open state. Lets a consumer
   * lazily mount content that misbehaves while hidden inside a closed `<details>`
   * (e.g. a Leaflet map, which initialises at 0×0 there — see WhereGroup.tsx).
   */
  onToggle?: (open: boolean) => void;
}

export const Accordion: React.FC<AccordionProps> = ({ title, defaultOpen, children, id, className, onToggle }) => {
  const detailsRef = useRef<HTMLDetailsElement>(null);

  // Native <details>/<summary> owns its open state after mount; this only seeds the initial value
  // (the DOM types here don't expose a JSX `defaultOpen` attribute for <details>).
  useEffect(() => {
    if (defaultOpen && detailsRef.current) detailsRef.current.open = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = detailsRef.current;
    if (!el || !onToggle) return;
    const handleToggle = () => onToggle(el.open);
    el.addEventListener('toggle', handleToggle);
    return () => el.removeEventListener('toggle', handleToggle);
  }, [onToggle]);

  return (
    <details ref={detailsRef} id={id} className={cn('group border-b border-line', className)}>
      <summary
        className={cn(
          'flex cursor-pointer list-none items-center justify-between gap-2 rounded-control py-3 text-sm font-medium text-ink',
          '[&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'
        )}
      >
        <span>{title}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      <div className="pb-4 text-sm text-ink">{children}</div>
    </details>
  );
};
